import React from 'react';
import { v4 } from 'uuid';
import { HandlingUnit } from '../types/handling_unit/handlingUnit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { HandlingUnitContext, HandlingUnitContextProps } from '../context/HandlingUnitContext';
import { HandlingUnitUtil } from '../types/handling_unit/handlingUnit.util';

const handlingUnitState = {
  items: new Map<string, HandlingUnit>(),
  loading: false,
};

export const handlingUnitSlice = createSlice({
  name: 'handlingUnit',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      handlingUnitState.items = new Map();
      handlingUnitState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<HandlingUnit[]>) => {
      handlingUnitState.items = new Map();
      action.payload.forEach(p => handlingUnitState.items.set(p.id, new HandlingUnit(p)));
      state.key = v4();
    },
    add: (state, action: PayloadAction<HandlingUnit[]>) => {
      action.payload.forEach(p => handlingUnitState.items.set(p.id, new HandlingUnit(p)));
      handlingUnitState.items = new Map(handlingUnitState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<HandlingUnit[]>) => {
      action.payload.forEach(p => handlingUnitState.items.delete(p.id));
      handlingUnitState.items = new Map(handlingUnitState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      handlingUnitState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface HandlingUnitProviderProps {
  children: React.ReactNode;
}

export default function HandlingUnitProvider({ children }: HandlingUnitProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.handlingUnits.key);

  const handlingUnitContext: HandlingUnitContextProps = {
    handlingUnits: handlingUnitState.items,
    handlingUnitsLoading: handlingUnitState.loading,
    setHandlingUnits: (handlingUnits: Map<string, HandlingUnit>) => {
      dispatch(handlingUnitSlice.actions.set([...handlingUnits.values()]));
    },
    breakPedigrees: HandlingUnitUtil.getPedigree(handlingUnitState.items, 'break'),
    bundlePedigrees: HandlingUnitUtil.getPedigree(handlingUnitState.items, 'bundle'),
    // TODO: Remove
    handlingUnitConnections: HandlingUnitUtil.getAllConnections(handlingUnitState.items),
  };

  return <HandlingUnitContext.Provider value={handlingUnitContext}>{children}</HandlingUnitContext.Provider>;
}
